import React, { useState } from 'react';
import {
    Box,
    Typography,
    IconButton,
    TextField,
    Button,
    Paper,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    ListItemSecondaryAction
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EventIcon from '@mui/icons-material/Event';
import DeleteIcon from '@mui/icons-material/Delete';
import { useContactUpdate } from '../../../hooks/useContactUpdate';
import { contactDetailStyles, animations } from './styles';

const ContactImportantDates = ({ contact, onContactUpdate, onSnackbar, t = {} }) => {
    const { updateContactPartial } = useContactUpdate();
    const [editingImportantDates, setEditingImportantDates] = useState(false);
    const [newImportantDate, setNewImportantDate] = useState({ date: '', description: '' });

    const handleAddImportantDate = async () => {
        if (!newImportantDate.date.trim()) return;
        
        try {
            const updatedDates = [...(contact.importantDates || []), {
                type: newImportantDate.description || 'Other',
                date: newImportantDate.date,
                notes: newImportantDate.description || ''
            }];
            
            const updatedContact = await updateContactPartial(contact._id, {
                importantDates: updatedDates
            }, contact);
            
            onContactUpdate(updatedContact);
            setNewImportantDate({ date: '', description: '' });
            setEditingImportantDates(false);
            
            if (onSnackbar) {
                onSnackbar({
                    open: true,
                    message: 'Important date added successfully',
                    severity: 'success'
                });
            }
        } catch (error) {
            console.error('Error adding important date:', error);
            if (onSnackbar) {
                onSnackbar({
                    open: true,
                    message: 'Error adding important date',
                    severity: 'error'
                });
            }
        }
    };

    const handleRemoveImportantDate = async (indexToRemove) => {
        try {
            const updatedDates = (contact.importantDates || []).filter((_, index) => index !== indexToRemove);
            const updatedContact = await updateContactPartial(contact._id, {
                importantDates: updatedDates
            }, contact);
            
            onContactUpdate(updatedContact);
            
            if (onSnackbar) {
                onSnackbar({
                    open: true,
                    message: 'Important date removed successfully',
                    severity: 'success'
                });
            }
        } catch (error) {
            console.error('Error removing important date:', error);
            if (onSnackbar) {
                onSnackbar({
                    open: true,
                    message: 'Error removing important date',
                    severity: 'error'
                });
            }
        }
    };

    return (
        <Box sx={{ ...contactDetailStyles.sectionContainer, ...animations.fadeInUp }}>
            <Box sx={contactDetailStyles.sectionHeader}>
                <Typography variant="h6" sx={contactDetailStyles.sectionTitle}>
                    <EventIcon fontSize="small" />
                    {t.importantDates || 'Important Dates'}
                </Typography>
                {!editingImportantDates && (
                    <IconButton 
                        size="small" 
                        sx={contactDetailStyles.inlineAction}
                        onClick={() => setEditingImportantDates(true)}
                    >
                        <AddIcon fontSize="small" />
                    </IconButton>
                )}
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                <Typography variant="subtitle2" sx={contactDetailStyles.subsectionLabel}>
                    Details
                </Typography>
            </Box>
            
            {editingImportantDates && (
                <Paper variant="outlined" sx={{ ...contactDetailStyles.softPanel, mb: 2 }}>
                    <TextField
                        fullWidth
                        label="Date"
                        value={newImportantDate.date}
                        onChange={(e) => setNewImportantDate(prev => ({ ...prev, date: e.target.value }))}
                        placeholder="e.g., 2024-01-15"
                        size="small"
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        fullWidth
                        label="Description"
                        value={newImportantDate.description}
                        onChange={(e) => setNewImportantDate(prev => ({ ...prev, description: e.target.value }))}
                        placeholder="e.g., Birthday, Anniversary"
                        size="small"
                        sx={{ mb: 2 }}
                    />
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            size="small"
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={handleAddImportantDate}
                            disabled={!newImportantDate.date.trim()}
                        >
                            Add
                        </Button>
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={() => {
                                setEditingImportantDates(false);
                                setNewImportantDate({ date: '', description: '' });
                            }}
                        >
                            Cancel
                        </Button>
                    </Box>
                </Paper>
            )}
            
            {contact.importantDates && contact.importantDates.length > 0 ? (
                contact.importantDates.map((dateObj, idx) => (
                    <ListItem key={`date-${idx}`} disablePadding sx={{ ...contactDetailStyles.listItem, pl: 0 }}>
                        <ListItemIcon>
                            <EventIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                            primary={
                                <Typography variant="body2" sx={contactDetailStyles.itemTitle}>
                                    {dateObj.date}
                                </Typography>
                            }
                            secondary={
                                <Typography variant="body2" sx={contactDetailStyles.itemMeta}>
                                    {dateObj.notes || dateObj.type || t.importantDate || 'Important Date'}
                                </Typography>
                            }
                        />
                        <ListItemSecondaryAction>
                            <IconButton 
                                edge="end" 
                                size="small" 
                                onClick={() => handleRemoveImportantDate(idx)}
                            >
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </ListItemSecondaryAction>
                    </ListItem>
                ))
            ) : (
                <Typography variant="body2" sx={contactDetailStyles.emptyText}>
                    No important dates added
                </Typography>
            )}
        </Box>
    );
};

export default ContactImportantDates; 
